import React from "react";
import { Dialog } from "@mui/material";

import ClientEditDialog from "../ClientEditDialog";
import MealPlanSelectDialog from "../MealPlanSelectDialog";
import CoachSelectDialog from "../CoachSelectDialog";
import BodyMeasurementsEditDialog from "../BodyMeasurementsEditDialog";
import MacroSelectSimpleDialog from "../MacroSelectSimpleDialog";
import NutritionGoals from "../NutritionGoals";
import CardioGoals from "../CardioGoals";
import CardioSelectDialog from "../CardioSelectDialog";
import ProtocolHistoryDialog from "./ProtocolHistoryDialog";

import UserData from "../../../../../data/UserData";
import ClientData from "../../../../../data/ClientData";
import { coachUserUpdateFromBasicInfo } from "../utils/coachClientUserUpdates";
import { sendMessageToUsersGroup } from "../../../../../data/MessageData";

const ClientDialogs = ({ client, organizationId, state, setState, handlers }) => {
  return (
    <>
      {/* Client Edit Dialog */}
      {state.clientEditOpen && (
        <ClientEditDialog
          client={client}
          onDialogClosed={async (data) => {
            setState((prev) => ({ ...prev, clientEditOpen: false }));

            if (data) {
              await UserData.updateUser(
                client.uid,
                coachUserUpdateFromBasicInfo(client, data),
              );

              await ClientData.updateClient(client.uid, {
                statistics: {
                  experience: data.experience,
                  height: data.height,
                  sex: data.sex,
                  goal: data.goal,
                  weight: data.weight,
                  startingWeight: data.startingWeight,
                  birthDate: data.birthDate,
                  activityLevel: data.activityLevel,
                  untrackedMeals: data.untrackedMeals,
                },
                language: data.language,
                status: data.status,
                tier: data.tier,
                statisticsChanged: new Date(),
                renewDate: data.renewDate,
                checkInDay: data.checkInDay,
                protocolAge: data.protocolAge,
              });
            }
          }}
        />
      )}

      {/* Meal Plan Select Dialog */}
      {state.mealPlanSelectOpen && (
        <MealPlanSelectDialog
          open={state.mealPlanSelectOpen}
          client={client}
          mealPlans={handlers.mealPlans}
          onClosed={async (mealPlanId) => {
            if (!mealPlanId || mealPlanId.length === 0) {
              setState((prev) => ({ ...prev, mealPlanSelectOpen: false }));
              return;
            }
            setState((prev) => ({ ...prev, mealPlanSelectOpen: false }));
            await ClientData.updateClient(client.uid, {
              mealPlanId,
              mealPlanChanged: new Date(),
            });

            const mealPlan = handlers.getMealPlanById(mealPlanId);
            if (mealPlan) {
              await sendMessageToUsersGroup({
                mustBeRegistered: true,
                uid: client.uid,
                message: `Your coach changed your meal plan to ${mealPlan.name}`,
              });
            }
          }}
        />
      )}

      {/* Coach Select Dialog */}
      {state.coachSelectOpen && (
        <CoachSelectDialog
          open={state.coachSelectOpen}
          client={client}
          onDialogClosed={(data) => {
            setState((prev) => ({ ...prev, coachSelectOpen: false }));

            if (data) {
              ClientData.updateClient(client.uid, {
                coaches: data.coaches,
                coachesChanged: new Date(),
              });

              ClientData.updateClientMessageGroup(client.uid, data.coaches);
            }
          }}
        />
      )}

      {/* Body Measurements Edit Dialog */}
      {state.bodyMeasurementOpen && (
        <BodyMeasurementsEditDialog
          client={client}
          report={state.dailyReport}
          onDialogClosed={() => {
            setState((prev) => ({ ...prev, bodyMeasurementOpen: false }));
          }}
        />
      )}

      {/* Macro Select Dialog */}
      {state.macroSelectOpen && (
        <MacroSelectSimpleDialog
          client={client}
          nutritionGoal={state.selectedNutritionGoal}
          open={state.macroSelectOpen}
          isEdit={Boolean(
            state.selectedNutritionGoal?.id &&
              (client?.nutritionGoals || []).some(
                (g) => g.id === state.selectedNutritionGoal.id,
              ),
          )}
          onDialogClosed={(data) => {
            setState((prev) => ({ ...prev, macroSelectOpen: false }));
            if (data) {
              handlers.handleNutritionGoalEdit(data);
            }
          }}
        />
      )}

      {/* Cardio Select Dialog */}
      {state.cardioSelectOpen && (
        <CardioSelectDialog
          client={client}
          cardioGoal={state.selectedCardioGoal}
          open={state.cardioSelectOpen}
          isEdit={Boolean(
            state.selectedCardioGoal?.id &&
              (client?.cardioGoals || []).some(
                (g) => g.id === state.selectedCardioGoal.id,
              ),
          )}
          onDialogClosed={(data) => {
            setState((prev) => ({ ...prev, cardioSelectOpen: false }));
            if (data) {
              handlers.handleCardioGoalEdit(data);
            }
          }}
        />
      )}

      {/* Nutrition Goals Dialog */}
      <Dialog
        open={state.nutritionGoalsDialogOpen}
        onClose={() =>
          setState((prev) => ({ ...prev, nutritionGoalsDialogOpen: false }))
        }
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "90vh",
            overflow: "hidden",
            maxWidth: 1200,
          },
        }}
      >
        <NutritionGoals
          client={client}
          style={{ margin: 0, borderRadius: 0, boxShadow: "none" }}
          onEdit={handlers.handleNutritionGoalEdit}
          onDelete={handlers.handleNutritionGoalDelete}
          onActivate={(nutritionGoal) => {
            handlers.handleNutritionGoalActivate(nutritionGoal);
            setState((prev) => ({ ...prev, nutritionGoalsDialogOpen: false }));
          }}
          onClose={() =>
            setState((prev) => ({ ...prev, nutritionGoalsDialogOpen: false }))
          }
        />
      </Dialog>

      {/* Cardio Goals Dialog */}
      <Dialog
        open={state.cardioGoalsDialogOpen}
        onClose={() =>
          setState((prev) => ({ ...prev, cardioGoalsDialogOpen: false }))
        }
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "90vh",
            overflow: "hidden",
          },
        }}
      >
        <CardioGoals
          client={client}
          style={{ margin: 0, borderRadius: 0, boxShadow: "none" }}
          onEdit={handlers.handleCardioGoalEdit}
          onDelete={handlers.handleCardioGoalDelete}
          onActivate={(cardioGoal) => {
            handlers.handleCardioGoalActivate(cardioGoal);
            setState((prev) => ({ ...prev, cardioGoalsDialogOpen: false }));
          }}
          onClose={() =>
            setState((prev) => ({ ...prev, cardioGoalsDialogOpen: false }))
          }
        />
      </Dialog>

      {/* Protocol History Dialog */}
      {state.protocolHistoryOpen && (
        <ProtocolHistoryDialog
          open={state.protocolHistoryOpen}
          client={client}
          organizationId={organizationId}
          onDialogClosed={() =>
            setState((prev) => ({ ...prev, protocolHistoryOpen: false }))
          }
        />
      )}

    </>
  );
};

export default ClientDialogs;
